import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { UserDashboardContext } from "../ProtectedRoute";
import LoadingComponent from "../Common/LoadingComponent";
import { fetchInsights } from "../AdminPanel/InsightManagement";
import { InsightTile } from "../CoTrader/InsightTile";
import { useInsightsWs } from "../CoTrader/Utilities/CoTraderUtils";
import MobileHeader from "../CoTrader/MobileHeader";
import { useTheme } from "../../ThemeProvider/ThemeContext";
import HomeChat from "./HomeChat";
import { getHomeUpdateKey, useHomeUpdatesFeed } from "./useHomeUpdatesFeed";

const HOME_UPDATES_SYMBOL = "HOME";
const HOME_UPDATES_LANGUAGE = "English";
const HOME_UPDATES_PANEL_HEIGHT = 360;

const HomeMobile = () => {
  const { tabs } = useContext(UserDashboardContext);
  const [insights, setInsights] = useState([]);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const homeUpdatesSymbolRef = useRef(HOME_UPDATES_SYMBOL);
  const homeChatRef = useRef(null);
  const {
    insights: updates,
    loading: updatesLoading,
    hasPendingIncoming,
    lastReceivedAt,
  } = useInsightsWs(
    HOME_UPDATES_SYMBOL,
    3,
    homeUpdatesSymbolRef,
    false,
    HOME_UPDATES_LANGUAGE,
  );
  const {
    displayUpdates,
    showLoading: showUpdatesLoading,
    showEmptyState: showUpdatesEmptyState,
  } = useHomeUpdatesFeed(updates, updatesLoading, {
    hasPendingIncoming,
    lastReceivedAt,
  });
  const { isDark } = useTheme();

  const styles = useMemo(() => {
    if (isDark) {
      return {
        pageBg: "#0B0F1A",
        contentBg: "#0B0F1A",
        divider: "#1E2642",
        sectionTitle: "#E8ECF8",
        sectionSubtitle: "#A6B0CF",
        cardBg: "#12172A",
        cardBorder: "#232B4A",
        muted: "#A6B0CF",
        link: "#4C7DFF",
      };
    }
    return {
      pageBg: "#FFFFFF",
      contentBg: "#FFFFFF",
      divider: "#E9ECEF",
      sectionTitle: "#0B1220",
      sectionSubtitle: "#5B6472",
      cardBg: "#FFFFFF",
      cardBorder: "#E9ECEF",
      muted: "#6C757D",
      link: "#0D6EFD",
    };
  }, [isDark]);

  useEffect(() => {
    const getHomeInsights = async () => {
      setInsightsLoading(true);
      try {
        const list = await fetchInsights();
        setInsights(list.insight_details || []);
      } catch (error) {
        console.error("Unable to fetch home insights:", error);
        setInsights([]);
      } finally {
        setInsightsLoading(false);
      }
    };

    void getHomeInsights();
  }, []);

  const SectionHeader = ({ title, subtitle }) => (
    <div className="px-2 pt-3 pb-2">
      <div
        className="fw-semibold"
        style={{ fontSize: "1.1rem", color: styles.sectionTitle }}
      >
        {title}
      </div>
      {subtitle ? (
        <div style={{ fontSize: "0.85rem", color: styles.sectionSubtitle }}>
          {subtitle}
        </div>
      ) : null}
    </div>
  );

  const Divider = () => (
    <div className="px-2">
      <div style={{ height: 1, width: "100%", background: styles.divider }} />
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Home | EMAlpha</title>
      </Helmet>

      <div
        className="d-flex flex-column w-100 h-100"
        style={{ background: styles.pageBg }}
      >
        <MobileHeader />
        <div style={{ height: 56 }} />

        <div
          className="flex-grow-1 overflow-auto"
          style={{ background: styles.contentBg }}
        >
          <div className="mb-3">
            <HomeChat
              ref={homeChatRef}
              origin="FXGPT"
              defaultCompanyIds={[""]}
              defaultDashboardName=""
              defaultCountries={[""]}
              showSuggestionsDefault={true}
              useApiSuggestionsOnly
              isDark={isDark}
              availableTabs={tabs}
            />
          </div>

          <SectionHeader
            title="Market Updates"
            subtitle="Live updates"
          />

          <div className="px-2 pb-3">
            <div
              style={{
                height: `${HOME_UPDATES_PANEL_HEIGHT}px`,
                overflowY: "auto",
                overflowX: "hidden",
                paddingRight: "4px",
              }}
            >
              {showUpdatesLoading ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <LoadingComponent />
                </div>
              ) : displayUpdates.length > 0 ? (
                <div className="row g-2">
                  {displayUpdates.map((update, index) => (
                    <div className="col-12" key={getHomeUpdateKey(update, index)}>
                      <InsightTile
                        title={update.title}
                        timeStamp={update.timestamp}
                        type={update.type}
                        description={update.description}
                        startTime={update.start_date}
                        isDark={isDark}
                        impactScore={update.impact_score}
                        question={update.question}
                        onAskQuestion={(questionText) =>
                          homeChatRef.current?.askQuestion(questionText)
                        }
                      />
                    </div>
                  ))}
                </div>
              ) : showUpdatesEmptyState ? (
                <div
                  className="d-flex justify-content-center align-items-center h-100 text-center"
                  style={{ fontSize: 15, color: styles.muted }}
                >
                  Waiting for the next live update...
                </div>
              ) : null}
            </div>
          </div>

          <Divider />

          <SectionHeader
            title="Insights"
            subtitle="Recent notes"
          />

          <div className="px-2 pb-3">
            {insightsLoading ? (
              <div className="d-flex justify-content-center align-items-center py-4">
                <LoadingComponent />
              </div>
            ) : (
              <div className="row g-2">
                {(insights || []).map((insight, index) => {
                  const imageUrl = insight.cover_image_url || insight.image;

                  return (
                    <div className="col-12" key={index}>
                    <a
                      href={insight.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`Read ${insight.title || "insight"}`}
                      style={{
                        display: "block",
                        borderRadius: 18,
                        overflow: "hidden",
                        background: styles.cardBg,
                        border: `1px solid ${styles.cardBorder}`,
                        color: "inherit",
                        textDecoration: "none",
                      }}
                    >
                      {imageUrl ? (
                        <div className="w-100">
                          <img
                            src={imageUrl}
                            alt={insight.title}
                            style={{
                              width: "100%",
                              height: 160,
                              objectFit: "contain",
                              background: isDark ? "#0B0F1A" : "#F5F8FC",
                              display: "block",
                            }}
                          />
                        </div>
                      ) : null}

                      <div style={{ padding: 12 }}>
                        <div
                          className="fw-semibold"
                          style={{ fontSize: "1rem", color: styles.sectionTitle }}
                        >
                          {insight.title}
                        </div>

                        <div className="mt-1">
                          <small style={{ color: styles.link, fontWeight: 700 }}>
                            {insight.date}
                          </small>
                        </div>

                        <div
                          style={{
                            fontSize: "0.92rem",
                            color: styles.muted,
                            marginTop: 8,
                          }}
                        >
                          {insight.text}
                        </div>

                        <div className="mt-2">
                          <span
                            style={{
                              fontSize: "0.95rem",
                              color: styles.link,
                              fontWeight: 700,
                            }}
                          >
                            Read More →
                          </span>
                        </div>
                      </div>
                    </a>
                    </div>
                  );
                })}

                <div className="col-12">
                  <div className="d-flex justify-content-center mt-2">
                    <a
                      href="https://www.emalpha.com/insights"
                      style={{
                        fontSize: "0.95rem",
                        color: styles.link,
                        fontWeight: 700,
                      }}
                    >
                      View All Insights →
                    </a>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div style={{ height: 12 }} />
        </div>
      </div>
    </>
  );
};

export default HomeMobile;
